<template>
  <section class="invoice-preview-wrapper">
    <b-row class="invoice-preview">
      <!-- Col: Left (Invoice Container) -->
      <b-col
        class="print-left-column"
        cols="12"
        xl="9"
        md="8"
      >
        <!-- Comparativa en tabla: tarjeta propia arriba de la cotizacion. Sustituye al
             desglose por partida, que se apaga desde InvoiceProductsTable. -->
        <quote-comparative-table
          v-if="showComparativeTable"
          :quote="quote"
          :items="items"
        />

        <b-card
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <div
            class="showOnprint"
          >
            <b-card-body
              v-if="roleName !== 'auctioneer'"
              class="invoice-padding pb-0"
            >
              <div
                class="print-info d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0"
              >
                <!-- Header: Left Content -->
                <div>
                  <div class="logo-wrapper">
                    <logo />
                  </div>
                  <div class="d-flex align-items-center justify-content-between mb-25">
                    <h6 class="mb-0">
                      {{ $t('invoiceTo') }}
                    </h6>
                    <b-button
                      v-if="canManageTaxInformation"
                      size="sm"
                      variant="outline-primary"
                      class="no-print"
                      @click="openTaxInformationModal"
                    >
                      {{ hasTaxInformationSelected ? 'Cambiar razón social' : 'Seleccionar razón social' }}
                    </b-button>
                  </div>
                  <p
                    v-if="isTaxInformationLocked"
                    class="text-muted small mb-25"
                  >
                    La razón social quedó bloqueada porque esta cotización ya quedó cerrada por pago aprobado y facturación.
                  </p>
                  <h6 class="mb-25">
                    {{ displayTaxInformation.corporate_name || '—' }}
                  </h6>
                  <p class="card-text mb-25">
                    {{ $t('address') }}: {{ displayTaxAddress }}
                  </p>
                  <p class="card-text mb-25">
                    RFC: {{ displayTaxInformation.rfc || '—' }}
                  </p>
                  <p class="card-text mb-25">
                    {{ $t('fiscalRegime') }}: {{ displayTaxInformation.fiscal_regime || '—' }}
                  </p>
                  <p class="card-text mb-0">
                    {{ $t('cfdiUse') }}: {{ displayTaxInformation.cfdi_use || '—' }}
                  </p>
                </div>

                <!-- Header: Right Content -->
                <div class="mt-md-0 mt-2">
                  <h4 class="invoice-title">
                    <div class="h5">
                      <span class="invoice-number">{{ project.name }}</span>
                    </div>
                    <div class="h5">
                      {{ $t('projectFolio') }}
                      <span class="invoice-number">#{{ project.folio }}</span>
                    </div>
                    <div class="h5">
                      {{ $t('quoteFolio') }}
                      <span class="invoice-number">#{{ quote.folio }}</span>
                    </div>
                  </h4>
                  <div class="invoice-date-wrapper">
                    <p class="invoice-date-title">
                      {{ $t('dateIssued') }}
                    </p>
                    <p class="invoice-date">
                      {{ quote.published_at | date }}
                    </p>
                  </div>
                </div>
              </div>
            </b-card-body>

            <div
              v-if="roleName === 'auctioneer'"
              class="logo-wrapper mb-0"
            >
              <logo />
            </div>

            <!-- Spacer -->
            <hr
              v-if="roleName !== 'auctioneer'"
              class="invoice-spacing"
            >

            <!-- Invoice Client & Payment Details -->
            <b-card-body class="invoice-padding pt-0">
              <b-row class="invoice-spacing shipment-payment-row">
                <!-- Col: Invoice To -->
                <b-col
                  cols="12"
                  xl="6"
                  class="p-0"
                >
                  <h6 class="mb-1">
                    {{ $t('shipmentTo') }}
                  </h6>
                  <p
                    v-if="project.google_maps_url"
                    class="card-text mb-25"
                  >
                    <a
                      :href="project.google_maps_url"
                      target="_blank"
                    >
                      {{ $t('Google Maps') }}
                    </a>
                  </p>
                  <p class="card-text mb-25">
                    {{ $t('address') }}: {{ (project.address && project.address.formatted_address) || '—' }}
                  </p>
                  <p
                    class="card-text mb-25"
                    style="white-space: pre-line"
                  >
                    <strong>{{ $t('observations') }}:</strong>
                    {{ project.observations || $t('noObservations') }}
                  </p>

                </b-col>

                <!-- Col: Payment Details -->
                <b-col
                  xl="6"
                  cols="12"
                  class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end payment-details-col"
                >
                  <div class="payment-details-box">
                    <h6 class="mb-2">
                      {{ $t('paymentDetails') }}
                    </h6>
                    <template v-if="showDualPaymentOptions">
                      <p class="payment-total-line mb-25">
                        <span class="payment-total-label">{{ $t('cashAmountLabel') }}:</span>
                        <span class="payment-total-value">${{ quote.cash_total_amount | money }}</span>
                      </p>
                      <p class="payment-total-line mb-0">
                        <span class="payment-total-label">{{ $t('creditAmountLabel') }}:</span>
                        <span class="payment-total-value text-warning">${{ quote.credit_total_amount | money }}</span>
                      </p>
                    </template>
                    <template v-else-if="showSelectedCreditWithoutSurcharge">
                      <p class="payment-total-line">
                        <span class="payment-total-label">{{ $t('creditNoSurchargeAmountLabel') }}:</span>
                        <span class="payment-total-value text-warning">${{ selectedAmount | money }}</span>
                      </p>
                    </template>
                    <p
                      v-else
                      class="payment-total-line"
                    >
                      <span class="payment-total-label">{{ selectedAmountLabel }}:</span>
                      <span class="payment-total-value">${{ selectedAmount | money }}</span>
                    </p>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>
          </div>

          <!-- AVISO EN PANTALLA -->
          <invoice-delivery-notice :purchased="hasFinalizedPurchase" class="no-print mx-2 mb-1" />

          <!-- AVISO EN IMPRESIÓN -->
          <invoice-delivery-notice :purchased="hasFinalizedPurchase" class="only-print mx-2 mb-1 print-notice" />

          <!-- Invoice Description: Table -->
          <!-- EN PANTALLA -->
          <invoice-products-table
            :hide-comparison="showComparativeTable"
            class="no-print"
            :products="items"
            :supplier-data="supplierData"
            :project-data="project"
            :quote-data="quote"
            @delete-user="refetch"
            @items-updated="refetch"
          />

          <!-- EN IMPRESIÓN: usa el MISMO componente -->
          <invoice-products-table
            :hide-comparison="showComparativeTable"
            class="only-print print-compact"
            :products="items"
            :supplier-data="supplierData"
            :project-data="project"
            :quote-data="quote"
            :print-mode="true"
            :responsive="false"
          />
          <div class="only-print print-link-note">
            Esta impresión es un resumen. Para ver la comparativa completa de proveedores
            y los precios actualizados, entra a la liga de la cotización:
            <span class="print-link-note__url">{{ quotePublicLink }}</span>
          </div>
          <div class="only-print print-totals-section">
            <div class="invoice-total-wrapper print-totals">
              <div class="invoice-total-item">
                <p class="invoice-total-title">
                  {{ $t('subtotal') }}:
                </p>
                <p class="invoice-total-amount">
                  ${{ quote.subtotal | money }}
                </p>
              </div>
              <div class="invoice-total-item">
                <p class="invoice-total-title">
                  {{ $t('shipping') }}:
                </p>
                <p class="invoice-total-amount">
                  ${{ quote.shipping_total | money }}
                </p>
              </div>
              <div class="invoice-total-item">
                <p class="invoice-total-title">
                  {{ $t('tax') }}:
                </p>
                <p class="invoice-total-amount">
                  ${{ quote.iva_total | money }}
                </p>
              </div>
              <template v-if="showCreditSurchargeBreakdown">
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('total') }}:
                  </p>
                  <p class="invoice-total-amount">
                    ${{ quote.cash_total_amount | money }}
                  </p>
                </div>
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('creditSurchargeLabel') }}:
                  </p>
                  <p class="invoice-total-amount text-warning">
                    ${{ quote.credit_surcharge_total | money }}
                  </p>
                </div>
                <hr class="my-50">
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('creditFinalAmountLabel') }}:
                  </p>
                  <p class="invoice-total-amount text-warning">
                    ${{ quote.credit_total_amount | money }}
                  </p>
                </div>
              </template>
              <template v-else-if="showDualPaymentOptions">
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('cashAmountLabel') }}:
                  </p>
                  <p class="invoice-total-amount">
                    ${{ quote.cash_total_amount | money }}
                  </p>
                </div>
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('creditAmountLabel') }}:
                  </p>
                  <p class="invoice-total-amount text-warning">
                    ${{ quote.credit_total_amount | money }}
                  </p>
                </div>
              </template>
              <div
                v-else
                class="invoice-total-item"
              >
                <p class="invoice-total-title">
                  {{ selectedAmountLabel }}:
                </p>
                <p :class="['invoice-total-amount', { 'text-warning font-weight-bold': showSelectedCreditWithoutSurcharge }]">
                  ${{ selectedAmount | money }}
                </p>
              </div>
            </div>
          </div>
          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0 totals-section no-print">
            <div class="totals-anchor mb-2">
              <div
                v-if="totalQuoteSavings > 0"
                class="quote-savings-panel"
              >
                <span class="quote-savings-panel__eyebrow">Ahorro estimado</span>
                <strong>${{ totalQuoteSavings | money }}</strong>
                <span class="quote-savings-panel__caption">
                  Contra el promedio de proveedores participantes en esta cotización.
                </span>
              </div>
              <div class="invoice-total-wrapper print-totals">
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('subtotal') }}:
                  </p>
                  <p class="invoice-total-amount">
                    ${{ quote.subtotal | money }}
                  </p>
                </div>
                <div class="invoice-total-item">
                  <p class="invoice-total-title d-flex align-items-center">
                    <span>{{ $t('shipping') }}:</span>
                    <b-button
                      v-if="canRemoveFreight"
                      size="sm"
                      variant="flat-danger"
                      class="ml-1 px-50 py-25 no-print"
                      :disabled="isRemovingFreight"
                      @click="removeFreight"
                    >
                      <feather-icon icon="TrashIcon" size="14" />
                    </b-button>
                  </p>
                  <p class="invoice-total-amount">
                    ${{ quote.shipping_total | money }}
                  </p>
                </div>
                <div class="invoice-total-item">
                  <p class="invoice-total-title">
                    {{ $t('tax') }}:
                  </p>
                <p class="invoice-total-amount">
                  ${{ quote.iva_total | money }}
                </p>
              </div>
                <template v-if="showCreditSurchargeBreakdown">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{ $t('total') }}:
                    </p>
                    <p class="invoice-total-amount">
                      ${{ quote.cash_total_amount | money }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{ $t('creditSurchargeLabel') }}:
                    </p>
                    <p class="invoice-total-amount text-warning">
                      ${{ quote.credit_surcharge_total | money }}
                    </p>
                  </div>
                  <hr class="my-50">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{ $t('creditFinalAmountLabel') }}:
                    </p>
                    <p class="invoice-total-amount text-warning">
                      ${{ quote.credit_total_amount | money }}
                    </p>
                  </div>
                </template>
                <template v-else-if="showDualPaymentOptions">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{ $t('cashAmountLabel') }}:
                    </p>
                    <p class="invoice-total-amount">
                      ${{ quote.cash_total_amount | money }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{ $t('creditAmountLabel') }}:
                    </p>
                    <p class="invoice-total-amount text-warning">
                      ${{ quote.credit_total_amount | money }}
                    </p>
                  </div>
                </template>
                <div
                  v-else
                  class="invoice-total-item"
                >
                  <p class="invoice-total-title">
                    {{ selectedAmountLabel }}:
                  </p>
                  <p :class="['invoice-total-amount', { 'text-warning font-weight-bold': showSelectedCreditWithoutSurcharge }]">
                    ${{ selectedAmount | money }}
                  </p>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <component
          :is="optionsBasedInRole"
          :key="`${optionsBasedInRole}-${roleName}`"
          :refetch="refetch"
          :quote="quote"
          :items="items"
          :project="project"
          :supplier-data="supplierData"
        />
      </b-col>
    </b-row>
    <b-modal
      :id="taxInformationModalId"
      title="Razón social de facturación"
      ok-title="Guardar"
      cancel-title="Cancelar"
      @ok="confirmTaxInformationSelection"
      @hidden="resetTaxInformationModal"
    >
      <div v-if="taxInformationLoading">
        Cargando razones sociales...
      </div>
      <div v-else-if="taxInformationOptions.length === 0">
        No hay razones sociales disponibles para este cliente.
      </div>
      <div v-else>
        <p class="mb-1">Selecciona la razón social para esta cotización.</p>
        <b-form-select
          v-model="selectedTaxInformationId"
          :options="taxInformationSelectOptions"
        />
      </div>
    </b-modal>
  </section>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { VBToggle } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Logo from '@/components/Logo.vue'
import InvoiceProductsTable from '@/components/invoice/InvoiceProductsTable.vue'
import CheckoutOptionsForAdmin from '@/components/invoice/CheckoutOptionsForAdmin.vue'
import CheckoutOptionsForGuest from '@/components/invoice/CheckoutOptionsForGuest.vue'
import CheckoutOptionsForUser from '@/components/invoice/CheckoutOptionsForUser.vue'
import PurchaseOrderOptionsForAuctioneer from '@/components/purchaseOrders/PurchaseOrderOptionsForAuctioneer.vue'
import PurchaseOrderOptionsForAdmin from '@/components/purchaseOrders/PurchaseOrderOptionsForAdmin.vue'
import router from '@/router'
import InvoiceDeliveryNotice from '@/components/invoice/InvoiceDeliveryNotice.vue'
import QuoteComparativeTable from '@/components/invoice/QuoteComparativeTable.vue'
import { quoteHasFinalizedPurchase } from '@/utils/quotePurchase'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    QuoteComparativeTable,
    Logo,
    InvoiceProductsTable,
    InvoiceDeliveryNotice,
    CheckoutOptionsForAdmin,
    CheckoutOptionsForGuest,
    CheckoutOptionsForUser,
    PurchaseOrderOptionsForAdmin,
    PurchaseOrderOptionsForAuctioneer,
  },

  props: {
    quote: {
      type: Object,
      default() {
        return {}
      },
    },
    project: {
      type: Object,
      default() {
        return {}
      },
    },
    address: {
      type: Object,
      default() {
        return {}
      },
    },
    items: {
      type: Array,
      default() {
        return []
      },
    },
    supplierData: {
      type: Array,
      default() {
        return []
      },
    },
    taxInfo: {
      type: Object,
      default() {
        return {}
      },
    },
    refetch: {
      type: Function,
      default() {
        return 0
      },
    },
  },
  setup(props, { root }) {
    const store = root.$store
    const paymentDetails = ref({})

    // Invoice Description
    // ? Your real data will contain this information
    const printInvoice = () => {
      window.print()
    }

    const supplierRoles = ['supplier_admin', 'auctioneer']

    const currentUser = computed(() => store.state.auth.currentUser)
    const roleName = computed(() => currentUser.value?.role_name || 'guest')
    const isAuthenticated = computed(() => Boolean(currentUser.value?.role_name))
    const quoteId = computed(() => props.quote?.id || root.$route.params.quoteId || null)
    const taxInformationModalId = computed(() => `desktopTaxInformation-${quoteId.value || 'quote'}`)
    const showComparativeTable = computed(() => props.quote?.comparative_layout === 'table')

    const quotePublicLink = computed(() => (
      quoteId.value ? `${window.location.origin}/checkout/${quoteId.value}` : ''
    ))
    const taxInformationLoading = ref(false)
    const taxInformationOptions = ref([])
    const selectedTaxInformationId = ref(null)
    const isRemovingFreight = ref(false)
    const storeQuote = computed(() => store.state.quotes?.quote || {})
    const isSameQuoteInStore = computed(() => {
      const currentId = quoteId.value
      const storeId = storeQuote.value?.id
      if (!currentId || !storeId) return false
      return String(storeId) === String(currentId)
    })
    const linkedTaxInformationId = computed(() => (
      props.quote?.tax_information_id
      || props.quote?.taxable_id
      || props.quote?.tax_information?.id
      || props.quote?.tax_information_attributes?.id
      || (isSameQuoteInStore.value
        ? (storeQuote.value?.tax_information_id || storeQuote.value?.taxable_id)
        : null)
      || null
    ))
    const effectiveTaxInformationId = computed(() => linkedTaxInformationId.value || selectedTaxInformationId.value || null)
    const selectedTaxInformationOption = computed(() => {
      if (!effectiveTaxInformationId.value) return null
      return (taxInformationOptions.value || []).find(
        item => String(item.id) === String(effectiveTaxInformationId.value),
      ) || null
    })
    const displayTaxInformation = computed(() => (
      selectedTaxInformationOption.value
      || props.quote?.tax_information_attributes
      || props.quote?.tax_information
      || props.taxInfo
      || {}
    ))
    const hasTaxInformationSelected = computed(() => Boolean(
      effectiveTaxInformationId.value
      || displayTaxInformation.value?.corporate_name
      || displayTaxInformation.value?.rfc,
    ))
    const isTaxInformationLocked = computed(() => Boolean(
      props.quote?.items_locked_for_accounting
      || (isSameQuoteInStore.value && storeQuote.value?.items_locked_for_accounting),
    ))
    const canManageTaxInformation = computed(() => (
      Boolean(quoteId.value)
      && isAuthenticated.value
      && !supplierRoles.includes(roleName.value)
      && !isTaxInformationLocked.value
    ))
    const taxInformationSelectOptions = computed(() => (taxInformationOptions.value || []).map(item => ({
      value: item.id,
      text: `${item.corporate_name || 'Razón social'}${item.rfc ? ` (${item.rfc})` : ''}`,
    })))
    const canRemoveFreight = computed(() => (
      roleName.value === 'admin'
      && Boolean(quoteId.value)
      && Number(props.quote?.shipping_total || 0) > 0
      && !isTaxInformationLocked.value
    ))

    function toast({ title, variant = 'success', icon = 'CheckCircleIcon' }) {
      root.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const getErrorPayload = error => error?.response?.data || error?.data || {}
    const getErrorMessage = (error, fallback = 'Ocurrió un error al procesar la solicitud.') => {
      const payload = getErrorPayload(error)
      if (Array.isArray(payload.messages) && payload.messages.length > 0) return payload.messages[0]
      if (typeof payload.error === 'string') return payload.error
      if (typeof error?.message === 'string') return error.message
      return fallback
    }

    function isValid(string) {
      return typeof string === 'string' && string.trim().length ? string.trim() : ''
    }

    const displayTaxAddress = computed(() => {
      const parts = [
        isValid(displayTaxInformation.value?.street),
        isValid(displayTaxInformation.value?.ext_number),
        isValid(displayTaxInformation.value?.postal_code),
        isValid(displayTaxInformation.value?.city),
        isValid(displayTaxInformation.value?.state),
      ].filter(Boolean)
      return parts.length ? parts.join(', ') : '—'
    })

    const loadTaxInformationOptions = async ({ silent = false } = {}) => {
      if (!quoteId.value || !canManageTaxInformation.value) return false
      try {
        taxInformationLoading.value = true
        const response = await store.dispatch('quotes/fetchQuoteTaxInformationOptions', { quoteId: quoteId.value })
        taxInformationOptions.value = response?.data || []
        selectedTaxInformationId.value = response?.selected_tax_information_id
          || response?.project_default_tax_information_id
          || response?.customer_default_tax_information_id
          || linkedTaxInformationId.value
          || taxInformationOptions.value?.[0]?.id
          || null
        return true
      } catch (error) {
        if (!silent) {
          toast({
            title: getErrorMessage(error, 'No se pudo cargar el catálogo de razones sociales.'),
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          })
        }
        return false
      } finally {
        taxInformationLoading.value = false
      }
    }

    const openTaxInformationModal = async () => {
      const loaded = await loadTaxInformationOptions()
      if (!loaded) return
      root.$bvModal.show(taxInformationModalId.value)
    }

    const confirmTaxInformationSelection = async modalEvent => {
      if (!selectedTaxInformationId.value) {
        modalEvent.preventDefault()
        toast({
          title: 'Selecciona una razón social para continuar.',
          variant: 'warning',
          icon: 'AlertTriangleIcon',
        })
        return
      }

      modalEvent.preventDefault()
      try {
        await store.dispatch('quotes/setQuoteTaxInformation', {
          quoteId: quoteId.value,
          taxInformationId: selectedTaxInformationId.value,
        })
        await store.dispatch('quotes/fetchQuote', quoteId.value)
        await loadTaxInformationOptions({ silent: true })
        if (typeof props.refetch === 'function') {
          await props.refetch()
        }
        root.$bvModal.hide(taxInformationModalId.value)
        toast({ title: 'Razón social actualizada.' })
      } catch (error) {
        toast({
          title: getErrorMessage(error, 'No fue posible actualizar la razón social.'),
          variant: 'danger',
          icon: 'AlertTriangleIcon',
        })
      }
    }

    const resetTaxInformationModal = () => {}

    const removeFreight = async () => {
      if (!canRemoveFreight.value || isRemovingFreight.value) return
      if (!window.confirm('¿Deseas eliminar el flete de esta cotización?')) return

      try {
        isRemovingFreight.value = true
        await store.dispatch('quotes/removeFreight', quoteId.value)
        if (typeof props.refetch === 'function') {
          await props.refetch()
        }
        toast({ title: 'Flete eliminado de la cotización.' })
      } catch (error) {
        toast({
          title: getErrorMessage(error, 'No fue posible eliminar el flete.'),
          variant: 'danger',
          icon: 'AlertTriangleIcon',
        })
      } finally {
        isRemovingFreight.value = false
      }
    }

    watch(
      quoteId,
      async value => {
        if (!value) return
        await loadTaxInformationOptions({ silent: true })
      },
      { immediate: true },
    )

    const optionsBasedInRole = computed(() => {
      // Options for purchase order view
      if (router.currentRoute.name === 'purchaseOrder') {
        if (roleName.value === 'admin') {
          return 'PurchaseOrderOptionsForAdmin'
        }
        // El supplier_admin caia hasta CheckoutOptionsForSupplier, que nunca existio, asi que
        // se quedaba sin imprimir, remision ni factura. Ocupa el mismo panel del subastador:
        // son las tres acciones que la orden de compra le ofrece a quien surte.
        if (supplierRoles.includes(roleName.value)) {
          return 'PurchaseOrderOptionsForAuctioneer'
        }
      }
      // Options for quote view
      if (!isAuthenticated.value) {
        return 'CheckoutOptionsForGuest'
      }
      if (roleName.value === 'admin') {
        return 'CheckoutOptionsForAdmin'
      }
      if (supplierRoles.includes(roleName.value)) {
        return 'CheckoutOptionsForSupplier'
      }
      return 'CheckoutOptionsForUser'
    })
    const money = n => {
      const num = Number(n || 0)
      try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(num)
      } catch {
        return `$${num.toFixed(2)}`
      }
    }

    return {
      paymentDetails,
      printInvoice,
      quotePublicLink,
      showComparativeTable,
      optionsBasedInRole,
      displayTaxAddress,
      displayTaxInformation,
      hasTaxInformationSelected,
      canManageTaxInformation,
      isTaxInformationLocked,
      openTaxInformationModal,
      taxInformationModalId,
      taxInformationLoading,
      taxInformationOptions,
      selectedTaxInformationId,
      taxInformationSelectOptions,
      confirmTaxInformationSelection,
      resetTaxInformationModal,
      canRemoveFreight,
      currentUser,
      isRemovingFreight,
      roleName,
      money,
      removeFreight,
    }
  },

  computed: {
    hasRecordedPaymentSelection() {
      return Boolean(this.quote?.selected_payment_mode)
    },
    showDualPaymentOptions() {
      return Boolean(this.quote?.customer_credit_eligible) && !this.hasRecordedPaymentSelection
    },
    showSelectedCreditWithoutSurcharge() {
      return this.quote?.selected_payment_mode === 'credit' && !this.showCreditSurchargeBreakdown
    },
    showCreditSurchargeBreakdown() {
      return Number(this.quote?.credit_surcharge_total || 0) > 0
        || Boolean(this.quote?.credit_pricing_applied)
    },
    selectedAmount() {
      return Number(this.quote?.selected_total_amount || this.quote?.total || 0)
    },
    selectedAmountLabel() {
      if (this.showSelectedCreditWithoutSurcharge) return this.$t('creditNoSurchargeAmountLabel')
      if (this.showCreditSurchargeBreakdown) return this.$t('creditFinalAmountLabel')
      return this.$t('total')
    },
    hasFinalizedPurchase() {
      return quoteHasFinalizedPurchase(this.quote)
    },
    totalQuoteSavings() {
      return this.roundMoney((this.items || []).reduce((total, item) => (
        total + Math.max(this.getItemSavings(item), 0)
      ), 0))
    },
    subtotal() {
      return this.items.reduce((prev, next) => ({
        total: parseFloat(prev.total) + parseFloat(next.total),
      }))
    },
  },
  methods: {
    roundMoney(value) {
      return Math.round(Number(value || 0) * 100) / 100
    },
    getCustomerUnitPrice(item) {
      const explicitCustomerPrice = Number(item?.customer_unit_price)
      if (Number.isFinite(explicitCustomerPrice)) return explicitCustomerPrice

      return Number(item?.unit_price || 0) + Number(item?.unit_commission_price || 0)
    },
    resolveComparisonUnitPrice(option) {
      const candidates = [
        option?.customer_unit_price,
        option?.unit_price,
        option?.supplier_unit_price,
        option?.price,
      ]

      const found = candidates
        .map(value => Number(value))
        .find(value => Number.isFinite(value) && value > 0)

      return found || 0
    },
    normalizeSupplierKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    },
    getSupplierLabel(item) {
      return item?.selected_supplier_branch_name
        || item?.selected_supplier_name
        || item?.supplier_name
        || item?.branch_name
        || item?.business_name
        || item?.supplier
        || ''
    },
    getItemSavings(item) {
      if (!item?.selected_supplier_branch_id || item?.active_status === false) return 0

      const unitPrices = Array.isArray(item?.unit_prices) ? item.unit_prices : []
      // Al cliente no le llegan las ofertas de cada proveedor; el backend le manda el ahorro ya calculado.
      if (unitPrices.length === 0) return this.roundMoney(Number(item?.estimated_savings) || 0)
      const selectedSupplierId = String(item.selected_supplier_branch_id || '')
      const selectedSupplierName = this.getSupplierLabel(item)
      const normalizedSelectedSupplierName = this.normalizeSupplierKey(selectedSupplierName)
      const selectedUnitPrice = this.roundMoney(this.getCustomerUnitPrice(item))
      const options = unitPrices
        .map(option => ({
          supplierId: option?.supplier_branch_id ? String(option.supplier_branch_id) : '',
          supplierName: option?.supplier_branch_name
            || option?.selected_supplier_branch_name
            || option?.supplier_name
            || option?.branch_name
            || option?.business_name
            || '',
          unitPrice: this.resolveComparisonUnitPrice(option),
        }))
        .filter(option => Number.isFinite(option.unitPrice) && option.unitPrice > 0)

      const hasSelectedOption = options.some(option => {
        const sameSupplierId = selectedSupplierId && option.supplierId === selectedSupplierId
        const sameSupplierName = normalizedSelectedSupplierName
          && this.normalizeSupplierKey(option.supplierName) === normalizedSelectedSupplierName
        const samePrice = this.roundMoney(option.unitPrice) === selectedUnitPrice
        return sameSupplierId || sameSupplierName || samePrice
      })

      if (!hasSelectedOption && selectedUnitPrice > 0) {
        options.push({
          supplierId: selectedSupplierId,
          supplierName: selectedSupplierName,
          unitPrice: selectedUnitPrice,
        })
      }

      if (options.length < 2 || selectedUnitPrice <= 0) return 0

      const participantPrices = options.map(option => option.unitPrice)
      const averageUnitPrice = participantPrices.reduce((sum, price) => sum + price, 0) / participantPrices.length
      const quantity = Number(item.quantity || 0)

      return this.roundMoney((averageUnitPrice - selectedUnitPrice) * quantity)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/pages/app-invoice.scss';

.invoice-preview {
  align-items: flex-start;
  row-gap: 1.5rem;
}

.invoice-preview-card {
  overflow: hidden;
  border: 1px solid rgba(92, 75, 135, 0.08);
  border-radius: 24px;
  box-shadow: 0 20px 45px rgba(53, 43, 89, 0.08);
}

.invoice-actions {
  position: sticky;
  top: 1.5rem;
}

.invoice-actions > * {
  border-radius: 22px;
}

.totals-section {
  padding-top: 0.5rem !important;
}

@media (max-width: 767.98px) {
  .invoice-actions {
    position: static;
  }
}
</style>

<style lang="scss">
@media print {
  // Global Styles
  body,
  text,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    background: transparent !important;
    color: #000 !important;
  }

  .print-info{
    display: flex;
    flex-direction: row !important;
  }

  .showOnprint {
    display: block !important;
  }
  .pagebreak {
    clear: both;
    page-break-after: always;
  }
  nav.header-navbar {
    display: none;
  }
  .print-left-column {
    width: 100% !important;
    max-width: 100%;
    flex-grow: 1;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }

  .noprint {
    display: none;
  }
}
/* Mostrar/ocultar */
.no-print { display: block; }
.only-print { display: none; }

@media print {
  .no-print { display: none !important; }
  .only-print { display: block !important; }

  /* Compactar tabla bootstrap-vue en impresión */
  .print-compact table,
  .print-compact .table {
    font-size: 11.5pt !important;
    border-collapse: collapse !important;
    width: 100% !important;
  }
  .print-compact .table th,
  .print-compact .table td {
    border: 1px solid #000 !important;
    padding: 6px 8px !important;
    vertical-align: top !important;
  }
  .print-compact .table th { font-weight: 700 !important; }
  .print-compact .table td.text-right,
  .print-compact .table th.text-right { text-align: right !important; }
  .print-compact .table td.text-center,
  .print-compact .table th.text-center { text-align: center !important; }

  /* Evitar que el encabezado se duplique raro: que thead se repita correctamente */
  .print-compact .table thead { display: table-header-group !important; }
  .print-compact .table tr { page-break-inside: avoid !important; }

  /* Mantener visible "Resumen de Costos" pero sin UI extra */
  .quote-cost-resume, .quote-cost-resume * {
    box-shadow: none !important;
  }

  /* OCULTAR menú/íconos flotantes/barras inferiores de la app */
  /* (cubre la mayoría de frameworks: fixed bottoms, navs, fabs, toolbars, etc.) */
  *[style*="position:fixed"],
  .fixed-bottom, .fixed-top, .sticky-bottom, .sticky-top,
  .app-bottom-bar, .bottom-nav, .navigation-bar, .nav-bottom, .menu-bar,
  .mobile-nav,
  .v-application--bottom-navigation, .floating-toolbar, .fab, .speed-dial,
  .footer, footer, .app-footer, .layout-footer, .navigation-footer {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    overflow: hidden !important;
  }

  /* Mostrar siempre el resumen, pero sin botones ni toggles */
  /* Mostrar siempre el resumen, pero sin botones ni toggles */
  .quote-cost-resume .btn,
  .quote-cost-resume [role="button"],
  .quote-cost-resume .b-button {
    display: none !important;
  }
  .quote-cost-resume {
    box-shadow: none !important;
  }

    /* Evitar recortes del wrapper responsive de tablas genéricas */
  .table-responsive,
  .b-table-sticky-header {
    overflow: visible !important;
  }

  /* Refuerzos anti-barras fijas al fondo/top */
  [style*="position: fixed"],
  [style*="position: sticky"] {
    display: none !important;
    height: 0 !important;
    visibility: hidden !important;
    overflow: hidden !important;
  }

  /* === Resumen de Costos: ancho fijo, corte de palabras y cabecera repetida === */
  .quote-cost-resume table {
    width: 100% !important;
    table-layout: fixed !important;      /* distribuye columnas y evita overflow */
    border-collapse: collapse !important;
  }
  .quote-cost-resume thead {
    display: table-header-group !important; /* repite encabezado al saltar de página */
  }
  .quote-cost-resume tr {
    page-break-inside: avoid !important;    /* no partir filas a la mitad */
  }
  .quote-cost-resume th,
  .quote-cost-resume td {
    padding: 6px 8px !important;
    white-space: normal !important;         /* permite múltiples líneas */
    word-break: break-word !important;      /* corta palabras largas si hace falta */
    vertical-align: top !important;
    font-size: 10pt !important;             /* un pelín más chico para que quepa */
  }

  /* Si el resumen trae wrapper responsive, que no recorte */
  .quote-cost-resume .table-responsive,
  .quote-cost-resume .b-table-sticky-header {
    overflow: visible !important;
  }

  /* --- Evitar que los flex .row/.col corten el contenido en impresión --- */
  .invoice-preview-wrapper .row,
  .invoice-preview-wrapper [class*="col-"] {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    float: none !important;
  }

  /* La tabla de productos y el resumen NO deben partir filas */
  .print-compact .table thead,
  .quote-cost-resume thead {
    display: table-header-group !important;
  }
  .print-compact .table tr,
  .quote-cost-resume tr {
    page-break-inside: avoid !important;    /* evita cortar filas */
    break-inside: avoid-page !important;     /* alias moderno */
  }
  .print-compact .table,
  .quote-cost-resume table {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  /* Mantén totales juntos (que no queden partidos) */
  .print-totals {
    page-break-inside: avoid !important;
  }

  /* --- Resumen de Costos: distribución, ancho y tipografía --- */
  .quote-cost-resume table {
    width: 100% !important;
    table-layout: fixed !important;     /* reparte ancho entre columnas */
    border-collapse: collapse !important;
    font-size: 9pt !important;          /* letra más compacta */
  }

  .quote-cost-resume th,
  .quote-cost-resume td {
    padding: 6px 8px !important;
    white-space: normal !important;     /* permite varias líneas */
    word-break: break-word !important;  /* parte palabras largas */
    vertical-align: top !important;
  }

  /* Primer columna (Proveedor) más ancha y alineada a la izquierda */
  .quote-cost-resume th:first-child,
  .quote-cost-resume td:first-child {
    width: 180px !important;            /* ajusta 160–200px según te convenga */
    text-align: left !important;
  }

  /* Oculta el panel derecho y cualquier botón en impresión */
  .invoice-actions,
  .invoice-actions * {
    display: none !important;
  }

  /* Cortafuegos: oculta botones sueltos que algún componente renderice */
  button,
  .btn,
  .b-button,
  [role="button"] {
    display: none !important;
  }

  /* === Totales a la derecha en impresión === */
  .invoice-total-wrapper.print-totals {
    width: 38% !important;        /* ajusta 30–45% según te convenga */
    margin-left: auto !important; /* empuja a la derecha */
    margin-right: 0 !important;
    float: right !important;      /* por si el display:block de cols lo afecta */
    text-align: right !important;
  }

  .invoice-total-wrapper.print-totals .invoice-total-item {
    display: flex !important;
    justify-content: space-between !important; /* título izq, monto der */
    gap: 12px !important;
  }

  .invoice-total-wrapper.print-totals .invoice-total-title {
    text-align: left !important;
    white-space: nowrap !important;
  }

  .invoice-total-wrapper.print-totals .invoice-total-amount {
    text-align: right !important;
    white-space: nowrap !important;
    font-weight: 700 !important;
  }

  /* Evita que el bloque se “caiga” por clear de otros elementos */
  .invoice-total-wrapper.print-totals::after {
    content: "";
    display: block;
    clear: both;
  }
  .print-notice {
    border: 1px solid #000 !important;
    border-left-width: 4px !important;
    background: transparent !important;
    padding: 10px 12px !important;
    border-radius: 6px !important;
    font-size: 11pt !important;
  }

  .print-notice * {
    color: #000 !important;
  }
}
@page {
  size: auto;               /* o letter, A4 */
  margin: 12mm 10mm 16mm;   /* ↑ dale más abajo (bottom) */
}

@media print {
  html, body {
    height: auto !important;
    overflow: visible !important;
  }
}
@media print {
  .invoice-preview-wrapper,
  .invoice-preview-card,
  .print-left-column {
    padding-bottom: 18mm !important; /* espacio extra para que NO recorte */
    margin-bottom: 0 !important;
    overflow: visible !important;
  }
}
@media print {
  .card,
  .card-body,
  .b-card-body,
  .table-responsive,
  .b-table-sticky-header {
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
  }
}
@media print {
  /* ✅ La tabla puede partirse entre páginas */
  .print-compact table,
  .print-compact .table {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  /* ✅ Evita cortar una fila a la mitad (pero permite que la tabla continúe) */
  .print-compact .table tr {
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
  }

  /* ✅ Mantén encabezado repetido en cada página */
  .print-compact .table thead {
    display: table-header-group !important;
  }

  /* ❌ IMPORTANTE: no fuerces avoid en td/th/tbody */
  .print-compact .table td,
  .print-compact .table th,
  .print-compact .table tbody {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }
}

@media print {
  body {
    font-family: 'Montserrat', sans-serif !important;
    font-size: 10.5pt !important;
    line-height: 1.35 !important;
    color: #101828 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .invoice-preview-card {
    border: none !important;
  }

  /* Tabla de productos: look profesional */
  .print-compact .table {
    border-collapse: collapse !important;
  }

  .print-compact .table th,
  .print-compact .table td {
    border: 1px solid #D0D5DD !important;
    padding: 6px 8px !important;
  }

  .print-compact .table th {
    background: #F2F4F7 !important;
    color: #101828 !important;
    font-size: 9pt !important;
    letter-spacing: 0.3px !important;
    text-transform: uppercase !important;
  }

  .print-compact .table tbody tr:nth-child(even) td {
    background: #FAFBFC !important;
  }

  .print-compact .table td .text-muted {
    color: #667085 !important;
  }

  .print-supplier {
    font-weight: 600 !important;
  }

  .print-compact .table td.text-right,
  .print-compact .table th.text-right,
  .print-compact .table td.text-nowrap {
    white-space: nowrap !important;
  }

  .print-phone {
    font-variant-numeric: tabular-nums;
  }

  /* Ajuste de anchos para lectura cómoda */
  .print-compact .table th:nth-child(1),
  .print-compact .table td:nth-child(1) {
    width: 26% !important;
  }
  .print-compact .table th:nth-child(2),
  .print-compact .table td:nth-child(2) {
    width: 18% !important;
  }
  .print-compact .table th:nth-child(3),
  .print-compact .table td:nth-child(3) {
    width: 6% !important;
  }
  .print-compact .table th:nth-child(4),
  .print-compact .table td:nth-child(4) {
    width: 12% !important;
  }
  .print-compact .table th:nth-child(7),
  .print-compact .table td:nth-child(7),
  .print-compact .table th:nth-child(8),
  .print-compact .table td:nth-child(8) {
    width: 12% !important;
  }

  /* Totales con caja sutil */
  .invoice-total-wrapper.print-totals {
    border: 1px solid #D0D5DD !important;
    border-radius: 8px !important;
    padding: 8px 10px !important;
    background: #FFFFFF !important;
  }
  .invoice-total-wrapper.print-totals hr {
    border-color: #D0D5DD !important;
  }

  /* Resumen de costos (print-only) */
  .print-cost-resume-table {
    border-collapse: collapse !important;
    table-layout: auto !important;
  }
  .print-cost-resume-table th,
  .print-cost-resume-table td {
    border: 1px solid #D0D5DD !important;
    padding: 6px 8px !important;
    font-size: 9.5pt !important;
  }
  .print-cost-resume-table th {
    background: #F2F4F7 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px !important;
  }

  /* Mantener visible la misma tabla matricial del resumen en impresión */
  .quote-cost-resume .print-cost-resume-matrix {
    display: table !important;
  }

  /* Nota de seguimiento más limpia */
  .print-notice {
    border-color: #101828 !important;
    background: #F8FAFC !important;
  }

  /* El PDF es un resumen: la comparativa completa vive en la liga. */
  .print-link-note {
    margin: 0.5rem 1.5rem 0.75rem !important;
    padding: 0.5rem 0.65rem !important;
    border: 1px solid #101828 !important;
    border-radius: 4px !important;
    background: #F8FAFC !important;
    font-size: 9pt !important;
    line-height: 1.35 !important;
    page-break-inside: avoid !important;
  }

  .print-link-note__url {
    font-weight: 700 !important;
    word-break: break-all !important;
  }
}

@media print {
  /* Resumen de costos: conservar tabla/estilo de web */
  .quote-cost-resume .no-print {
    display: block !important;
  }

  .quote-cost-resume .only-print {
    display: none !important;
  }

  .quote-cost-resume h2 {
    color: #5E5873 !important;
    font-size: 1.714rem !important;
    font-weight: 500 !important;
  }

  .quote-cost-resume .table {
    width: 100% !important;
    table-layout: auto !important;
    font-size: 1rem !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
  }

  .quote-cost-resume th,
  .quote-cost-resume td {
    width: auto !important;
  }

  .quote-cost-resume .table thead th {
    background-color: #F3F2F7 !important;
    color: #6E6B7B !important;
    border-color: #EBE9F1 !important;
    font-size: 0.857rem !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    white-space: normal !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
    line-height: 1.15 !important;
  }

  .quote-cost-resume .table td {
    color: #6E6B7B !important;
    border-color: #EBE9F1 !important;
    font-size: 1rem !important;
    padding: 0.72rem 1rem !important;
    white-space: nowrap !important;
  }

  .quote-cost-resume .table-responsive,
  .quote-cost-resume .b-table-sticky-header {
    overflow-x: visible !important;
    overflow-y: visible !important;
  }

  /* Resumen comparativo transpuesto: evitar corte de proveedores al imprimir */
  .quote-cost-resume .print-cost-resume-matrix {
    width: 100% !important;
    table-layout: fixed !important;
  }

  .quote-cost-resume .print-cost-resume-matrix th,
  .quote-cost-resume .print-cost-resume-matrix td {
    padding: 3px 4px !important;
    font-size: 7.1pt !important;
    line-height: 1.2 !important;
  }

  .quote-cost-resume .print-cost-resume-matrix thead {
    display: table-header-group !important;
  }

  .quote-cost-resume .print-cost-resume-matrix tr {
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
  }

  .quote-cost-resume .print-cost-resume-matrix th {
    white-space: normal !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
  }

  .quote-cost-resume .print-cost-resume-matrix th:first-child {
    width: 20% !important;
    text-align: left !important;
  }

  .quote-cost-resume .print-cost-resume-matrix td:first-child {
    width: 20% !important;
    text-align: left !important;
    white-space: normal !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
  }

  .quote-cost-resume .print-cost-resume-matrix th:not(:first-child) {
    text-align: center !important;
    white-space: pre-line !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
    font-size: 6.8pt !important;
    line-height: 1.1 !important;
  }

  .quote-cost-resume .print-cost-resume-matrix td:not(:first-child) {
    text-align: right !important;
    white-space: nowrap !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
    font-variant-numeric: tabular-nums !important;
  }

  .quote-cost-resume .print-cost-resume-matrix td.price-cell.empty-price {
    text-align: center !important;
  }

}

.payment-details-box {
  text-align: right;
}

.payment-total-line {
  margin-bottom: 0;
}

.payment-total-label {
  margin-right: 0.35rem;
}

.payment-total-value {
  font-weight: 700;
}

.totals-anchor {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  justify-content: space-between;
}

.quote-savings-panel {
  background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%);
  border: 1px solid #86efac;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.08);
  color: #047857;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  max-width: 360px;
  min-height: 86px;
  padding: 0.85rem 1rem;
}

.quote-savings-panel__eyebrow {
  color: #065f46;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.15;
}

.quote-savings-panel strong {
  color: #047857;
  font-size: 1.45rem;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  line-height: 1.1;
  margin-top: 0.25rem;
}

.quote-savings-panel__caption {
  color: #059669;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 0.35rem;
  max-width: 290px;
}

.totals-anchor .invoice-total-wrapper.print-totals {
  flex: 0 0 320px;
  margin-left: auto;
}

@media (max-width: 991.98px) {
  .totals-anchor {
    align-items: stretch;
    flex-direction: column;
  }

  .quote-savings-panel {
    max-width: none;
  }

  .totals-anchor .invoice-total-wrapper.print-totals {
    flex: 0 0 auto;
    margin-left: auto;
    width: 320px;
  }
}

@media print {
  .payment-details-col {
    display: flex !important;
    justify-content: flex-end !important;
    align-items: flex-start !important;
  }

  .payment-details-box {
    width: 100% !important;
    text-align: right !important;
  }

  .payment-total-line {
    font-size: 11pt !important;
    font-weight: 600 !important;
  }

  .print-compact,
  .print-compact .table {
    margin-bottom: 0 !important;
  }

  .totals-section {
    padding-top: 4px !important;
    padding-bottom: 0 !important;
  }

  .totals-anchor {
    margin-top: 4px !important;
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
  }

  .invoice-total-wrapper.print-totals {
    width: 320px !important;
    max-width: 100% !important;
    float: none !important;
    margin: 0 !important;
  }
}

@media print {
  .invoice-preview-wrapper .shipment-payment-row {
    display: flex !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
    gap: 12px !important;
  }

  .shipment-payment-row > [class*="col-"]:first-child {
    flex: 0 0 68% !important;
    width: 68% !important;
    max-width: 68% !important;
  }

  .shipment-payment-row > .payment-details-col {
    flex: 0 0 32% !important;
    width: 32% !important;
    max-width: 32% !important;
    margin-left: auto !important;
    text-align: right !important;
  }

  .payment-details-box {
    width: max-content !important;
    margin-left: auto !important;
  }

  .payment-total-line {
    margin: 0 !important;
    white-space: nowrap !important;
  }

  .totals-section {
    display: none !important;
  }

  .print-totals-section {
    display: flex !important;
    justify-content: flex-end !important;
    margin-top: 2mm !important;
    page-break-before: avoid !important;
    break-before: avoid-page !important;
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
  }

  .print-totals-section .invoice-total-wrapper.print-totals {
    width: 320px !important;
    max-width: 100% !important;
    margin: 0 !important;
    float: none !important;
  }

  .print-compact,
  .print-compact .table,
  .print-compact .table-responsive {
    margin-bottom: 0 !important;
  }

  .print-compact {
    page-break-after: avoid !important;
    break-after: avoid-page !important;
  }

  .invoice-preview-wrapper,
  .invoice-preview-card,
  .print-left-column {
    padding-bottom: 2mm !important;
  }
}

@media print {
  /* Si el resumen completo no cabe, muévelo íntegro a la siguiente página */
  .print-cost-resume-block {
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
    page-break-before: auto !important;
    break-before: auto !important;
  }

  .print-cost-resume-block .quote-cost-resume,
  .print-cost-resume-block .quote-cost-resume .table-responsive,
  .print-cost-resume-block .quote-cost-resume .b-table-sticky-header,
  .print-cost-resume-block .quote-cost-resume table {
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
  }

  .print-cost-resume-block .quote-cost-resume tr,
  .print-cost-resume-block .quote-cost-resume th,
  .print-cost-resume-block .quote-cost-resume td {
    page-break-inside: avoid !important;
    break-inside: avoid-page !important;
  }
}

</style>
